/**
 * Shared notification template registry + substitution engine.
 *
 * The SINGLE SOURCE OF TRUTH for default saleroom notification copy. Imported
 * by BOTH the dispatcher (functions/_lib/notify.ts, which sends) AND the admin
 * console (src/, which lets a team member override the copy). It is therefore
 * pure data + pure functions only - no DOM, no Worker, no Node APIs - so both
 * the Vite app bundle and the Pages Functions bundle can include it.
 *
 * Model:
 *   - DEFAULT_TEMPLATES holds the shipped copy as TEMPLATE STRINGS with
 *     {{token}} placeholders and {{#token}}..{{/token}} / {{^token}}..{{/token}}
 *     optional sections (Mustache-lite). Email bodies are PLAIN TEXT with
 *     blank-line paragraphs and **bold** - never raw HTML; the branded shell +
 *     HTML-escaping are applied by the renderer, so an admin can never inject
 *     markup.
 *   - notification_templates (DB, migration 096) stores OVERRIDES keyed by
 *     (event_type, channel, variant). A missing row = use the default here, so
 *     "reset to default" in the UI is just deleting the row.
 *   - NOTIF_EVENTS is the registry the console renders from: per-event label,
 *     description, channels, variants and the whitelisted tokens (with sample
 *     values for live preview).
 */

export type NotifyEvent =
  | 'outbid' | 'ending_soon' | 'won' | 'lost' | 'test'
  | 'payment_action' | 'payment_expired';

export type NotifyChannel = 'email' | 'sms';

export interface NotifyPayload {
  lot_name?: string;
  lot_number?: string;
  slug?: string;
  amount_cents?: number | null;
  shipping_cost?: number | null;
  ends_at?: string;
  bought_now?: boolean;
  deadline?: string;          // ISO; when the buyer's 48h window lapses
  reason?: string;            // charge status: 'failed' | 'requires_action'
  hours_left?: number;
  // test rows can carry overrides
  email?: string;
  phone?: string;
}

// ---- Pure formatting helpers (moved here so both sides share them) ---------

export function formatUsd(cents: number | null | undefined): string {
  if (cents == null) return '$ - ';
  const dollars = cents / 100;
  return cents % 100 === 0 ? `$${dollars.toFixed(0)}` : `$${dollars.toFixed(2)}`;
}

export function lotUrl(origin: string, payload: NotifyPayload): string {
  return payload.slug ? `${origin}/p/${payload.slug}` : `${origin}/sales-room`;
}

export function saleroomUrl(origin: string): string {
  return `${origin}/sales-room`;
}

/** Human deadline like "Sat, 28 Jun, 3:14 PM UTC"; falls back to the raw ISO. */
export function formatDeadline(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: 'numeric', minute: '2-digit', timeZone: 'UTC', timeZoneName: 'short',
  });
}

/** "About 12h left." style fragment (NO leading space - the template adds it
 *  via its optional section), or "" when unknown. Plain ASCII for SMS. */
export function hoursLeftFragment(hoursLeft: number | null | undefined): string {
  if (hoursLeft == null || hoursLeft <= 0) return '';
  if (hoursLeft <= 1) return 'Less than an hour left.';
  return `About ${Math.round(hoursLeft)}h left.`;
}

// ---- Substitution engine ----------------------------------------------------

export type TokenValues = Record<string, string | null | undefined>;

function isTruthy(v: string | null | undefined): boolean {
  return v != null && v !== '' && v !== 'false' && v !== '0';
}

/**
 * Render a template string against token values. Supports:
 *   {{token}}              - replaced with the value (missing → '')
 *   {{#token}}..{{/token}} - inner kept only when the value is truthy
 *   {{^token}}..{{/token}} - inner kept only when the value is falsy
 * Values are inserted RAW (plain text). For email this output is then
 * HTML-escaped wholesale by renderEmailBodyHtml, so values can never inject
 * markup. Sections are resolved before tokens; one level of nesting is enough
 * for our copy so we resolve sections iteratively until stable.
 */
export function renderTemplate(tpl: string, values: TokenValues): string {
  let out = tpl;
  // Resolve sections repeatedly until none remain (handles sequential, not
  // deeply-nested, sections - which is all our copy uses).
  const sectionRe = /\{\{([#^])(\w+)\}\}([\s\S]*?)\{\{\/\2\}\}/;
  let guard = 0;
  while (sectionRe.test(out) && guard++ < 50) {
    out = out.replace(sectionRe, (_m, kind: string, key: string, inner: string) => {
      const truthy = isTruthy(values[key]);
      const keep = kind === '#' ? truthy : !truthy;
      return keep ? inner : '';
    });
  }
  // Replace remaining simple tokens.
  out = out.replace(/\{\{(\w+)\}\}/g, (_m, key: string) => {
    const v = values[key];
    return v == null ? '' : String(v);
  });
  return out;
}

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Render a PLAIN-TEXT email body template into safe inner HTML: substitute
 * tokens, HTML-escape the whole thing (so neither admin copy nor member data
 * can inject markup), then apply the only two bits of formatting we allow -
 * **bold** and blank-line paragraphs. Returns the inner HTML that drops into
 * the branded shell's body slot.
 */
export function renderEmailBodyHtml(tpl: string, values: TokenValues): string {
  const text = renderTemplate(tpl, values);
  const paragraphs = escapeHtml(text)
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      const withBold = p
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\n/g, '<br>');
      return `<p style="margin:0 0 14px 0;font-size:16px;line-height:1.65;">${withBold}</p>`;
    });
  return paragraphs.join('');
}

// ---- The default templates --------------------------------------------------

export interface EmailTemplate {
  subject: string;
  preview: string;
  body: string;          // plain text, **bold**, {{tokens}}, blank-line paras
  cta_label: string | null;
  cta_target: 'lot' | 'saleroom' | null;
}
export interface SmsTemplate {
  body: string;          // plain text, single GSM-7 segment target
}

type EventDefaults = {
  email: Record<string, EmailTemplate>;   // keyed by variant
  sms: Record<string, SmsTemplate>;
};

export const DEFAULT_TEMPLATES: Record<NotifyEvent, EventDefaults> = {
  outbid: {
    email: {
      default: {
        subject: `You've been outbid on "{{lot_name}}"`,
        preview: `The bid is now {{amount}}.`,
        body:
`Hi {{first_name}},

Someone has placed a higher bid on "{{lot_name}}". The bid now stands at **{{amount}}**.

If you still want it, you can place a new bid while the lot is live.`,
        cta_label: 'Bid again',
        cta_target: 'lot',
      },
    },
    sms: {
      default: {
        body: `Converted Closet: you've been outbid on "{{lot_name}}". The bid is now {{amount}}. Bid again: {{lot_url}}`,
      },
    },
  },

  ending_soon: {
    email: {
      default: {
        subject: `Ending soon: "{{lot_name}}"`,
        preview: `This lot closes in about an hour.`,
        body:
`Hi {{first_name}},

"{{lot_name}}" closes in about an hour{{#amount}} - the bid currently stands at **{{amount}}**{{/amount}}.

If you want it, now is the moment.`,
        cta_label: 'View the lot',
        cta_target: 'lot',
      },
    },
    sms: {
      default: {
        body: `Converted Closet: "{{lot_name}}" ends in about an hour.{{#amount}} Top bid {{amount}}.{{/amount}} {{lot_url}}`,
      },
    },
  },

  won: {
    email: {
      default: {
        subject: `You won "{{lot_name}}"`,
        preview: `Winning bid {{amount}}.`,
        body:
`Hi {{first_name}},

Congratulations - {{#bought_now}}you bought{{/bought_now}}{{^bought_now}}you won{{/bought_now}} "{{lot_name}}" at **{{amount}}**{{#shipping}} plus {{shipping}} postage{{/shipping}}.

We'll confirm payment against your card on file and follow up with shipping details.`,
        cta_label: 'View the lot',
        cta_target: 'lot',
      },
    },
    sms: {
      default: {
        body: `Converted Closet: you won "{{lot_name}}" at {{amount}}! We'll be in touch about payment. {{lot_url}}`,
      },
    },
  },

  lost: {
    email: {
      default: {
        subject: `The auction for "{{lot_name}}" has ended`,
        preview: `This lot has closed.`,
        body:
`Hi {{first_name}},

The auction for "{{lot_name}}" has closed, and another bid won the lot this time.

New lots are listed in the Saleroom regularly - keep an eye out.`,
        cta_label: 'Browse the Saleroom',
        cta_target: 'saleroom',
      },
    },
    sms: {
      default: {
        body: `Converted Closet: the auction for "{{lot_name}}" has ended. {{lot_url}}`,
      },
    },
  },

  payment_action: {
    email: {
      sca: {
        subject: `Finish your payment for "{{lot_name}}"`,
        preview: `A quick bank security check is needed.`,
        body:
`Hi {{first_name}},

You won "{{lot_name}}", but your bank asked for an extra security check before the payment of **{{amount}}** could go through.

Please complete the check{{#deadline}} by **{{deadline}}**{{/deadline}} to secure your lot. After that the deadline passes, the lot may be released.`,
        cta_label: 'Complete payment',
        cta_target: 'lot',
      },
      declined: {
        subject: `Payment needed for "{{lot_name}}"`,
        preview: `Your card needs attention.`,
        body:
`Hi {{first_name}},

You won "{{lot_name}}", but the payment of **{{amount}}** didn't go through - your card was declined.

Please update your card and try again{{#deadline}} by **{{deadline}}**{{/deadline}} to secure your lot. After that the deadline passes, the lot may be released.`,
        cta_label: 'Update card and pay',
        cta_target: 'lot',
      },
    },
    sms: {
      sca: {
        body: `Converted Closet: "{{lot_name}}" needs a quick bank security check to finish payment.{{#hours_left}} {{hours_left}}{{/hours_left}} Complete it to keep your lot: {{lot_url}}`,
      },
      declined: {
        body: `Converted Closet: payment for "{{lot_name}}" didn't go through.{{#hours_left}} {{hours_left}}{{/hours_left}} Update your card and try again to keep your lot: {{lot_url}}`,
      },
    },
  },

  payment_expired: {
    email: {
      default: {
        subject: `The lot "{{lot_name}}" has been released`,
        preview: `The time to pay has passed.`,
        body:
`Hi {{first_name}},

The time to complete payment for "{{lot_name}}" has passed, so we've released the lot.

If you still wanted it, reply to this email and we'll see what we can do - and keep an eye on the Saleroom for new lots.`,
        cta_label: 'Browse the Saleroom',
        cta_target: 'saleroom',
      },
    },
    sms: {
      default: {
        body: `Converted Closet: the time to complete payment for "{{lot_name}}" has passed, so the lot has been released. Questions? {{lot_url}}`,
      },
    },
  },

  test: {
    email: {
      default: {
        subject: `Test notification from Converted Closet`,
        preview: `Saleroom email notifications are working.`,
        body:
`Hi {{first_name}},

This is a test of the saleroom notification pipeline. If you're reading this, email delivery works.`,
        cta_label: null,
        cta_target: null,
      },
    },
    sms: {
      default: {
        body: `Converted Closet: test message - saleroom SMS notifications are working.`,
      },
    },
  },
};

// ---- The registry the console renders from ----------------------------------

export interface TokenDef { key: string; label: string; sample: string; }
export interface VariantDef { variant: string; label: string; }
export interface NotifEventDef {
  event: NotifyEvent;
  label: string;
  description: string;            // "when it fires"
  channels: NotifyChannel[];
  variants: VariantDef[];         // 1 for most; payment_action has 2
  tokens: TokenDef[];             // whitelist available to this event's copy
}

// Sample values for the live preview, shared across events for consistency.
const SAMPLE: Record<string, string> = {
  lot_name: 'Silk Wrap Dress',
  lot_number: '12',
  amount: '$48',
  shipping: '$6',
  lot_url: 'https://club.convertedcloset.com/p/silk-wrap-dress',
  saleroom_url: 'https://club.convertedcloset.com/sales-room',
  first_name: 'Kate',
  deadline: 'Sat, 28 Jun, 3:14 PM UTC',
  hours_left: 'About 12h left.',
  bought_now: '',
};
const T = (key: string, label: string): TokenDef => ({ key, label, sample: SAMPLE[key] ?? '' });

const TOK = {
  lot_name: T('lot_name', 'Lot name'),
  lot_number: T('lot_number', 'Lot number'),
  amount: T('amount', 'Amount / top bid'),
  shipping: T('shipping', 'Postage'),
  lot_url: T('lot_url', 'Link to the lot'),
  saleroom_url: T('saleroom_url', 'Link to the Saleroom'),
  first_name: T('first_name', 'Member first name'),
  deadline: T('deadline', 'Payment deadline'),
  hours_left: T('hours_left', 'Hours left fragment'),
  bought_now: T('bought_now', 'Bought via Buy Now (yes/no)'),
};

export const NOTIF_EVENTS: NotifEventDef[] = [
  {
    event: 'outbid',
    label: 'Outbid',
    description: 'Someone places a higher bid - tells the member they just lost the lead.',
    channels: ['email', 'sms'],
    variants: [{ variant: 'default', label: 'Default' }],
    tokens: [TOK.first_name, TOK.lot_name, TOK.amount, TOK.lot_url],
  },
  {
    event: 'ending_soon',
    label: 'Ending soon',
    description: 'About an hour before a lot closes - to watchers and bidders.',
    channels: ['email', 'sms'],
    variants: [{ variant: 'default', label: 'Default' }],
    tokens: [TOK.first_name, TOK.lot_name, TOK.amount, TOK.lot_url],
  },
  {
    event: 'won',
    label: 'Won',
    description: 'A member wins a lot (or buys it via Buy Now).',
    channels: ['email', 'sms'],
    variants: [{ variant: 'default', label: 'Default' }],
    tokens: [TOK.first_name, TOK.lot_name, TOK.amount, TOK.shipping, TOK.bought_now, TOK.lot_url],
  },
  {
    event: 'lost',
    label: 'Lost',
    description: 'A lot a member bid on closes and another bid won it.',
    channels: ['email', 'sms'],
    variants: [{ variant: 'default', label: 'Default' }],
    tokens: [TOK.first_name, TOK.lot_name, TOK.lot_url, TOK.saleroom_url],
  },
  {
    event: 'payment_action',
    label: 'Payment needed',
    description: 'A winner\'s card needs attention - a bank security check (SCA) or a declined card.',
    channels: ['email', 'sms'],
    variants: [
      { variant: 'sca', label: 'Bank security check (SCA)' },
      { variant: 'declined', label: 'Card declined' },
    ],
    tokens: [TOK.first_name, TOK.lot_name, TOK.amount, TOK.deadline, TOK.hours_left, TOK.lot_url],
  },
  {
    event: 'payment_expired',
    label: 'Payment expired',
    description: 'The 48h payment window lapsed - the lot is released.',
    channels: ['email', 'sms'],
    variants: [{ variant: 'default', label: 'Default' }],
    tokens: [TOK.first_name, TOK.lot_name, TOK.lot_url, TOK.saleroom_url],
  },
  {
    event: 'test',
    label: 'Test',
    description: 'Pipeline smoke test sent from the admin tester.',
    channels: ['email', 'sms'],
    variants: [{ variant: 'default', label: 'Default' }],
    tokens: [TOK.first_name],
  },
];

export function eventDef(event: NotifyEvent): NotifEventDef | undefined {
  return NOTIF_EVENTS.find((e) => e.event === event);
}

/** The set of valid token keys for an event (for editor validation). */
export function allowedTokenKeys(event: NotifyEvent): Set<string> {
  return new Set((eventDef(event)?.tokens ?? []).map((t) => t.key));
}

/** Sample token values for previewing an event's templates in the console. */
export function sampleValues(event: NotifyEvent): TokenValues {
  const v: TokenValues = {};
  for (const t of eventDef(event)?.tokens ?? []) v[t.key] = t.sample;
  // 'won' preview: show the non-Buy-Now wording + a postage line.
  if (event === 'won') { v.bought_now = ''; v.shipping = SAMPLE.shipping; }
  return v;
}

// ---- Build the live token values from a real payload (dispatcher side) ------

/** Map a queue row payload to token values for rendering an actual send. */
export function buildTokenValues(
  payload: NotifyPayload,
  origin: string,
  firstName: string | null,
): TokenValues {
  const hasAmount = payload.amount_cents != null;
  const hasShipping = payload.shipping_cost != null && payload.shipping_cost > 0;
  return {
    lot_name: payload.lot_name ?? 'your lot',
    lot_number: payload.lot_number ?? '',
    amount: hasAmount ? formatUsd(payload.amount_cents) : '',
    shipping: hasShipping ? formatUsd(payload.shipping_cost) : '',
    lot_url: lotUrl(origin, payload),
    saleroom_url: saleroomUrl(origin),
    first_name: firstName || 'there',
    deadline: payload.deadline ? formatDeadline(payload.deadline) : '',
    hours_left: hoursLeftFragment(payload.hours_left),
    bought_now: payload.bought_now ? 'yes' : '',
  };
}

/** Which template variant a payload selects. Only payment_action branches. */
export function variantForPayload(event: NotifyEvent, payload: NotifyPayload): string {
  if (event === 'payment_action') {
    return payload.reason === 'requires_action' ? 'sca' : 'declined';
  }
  return 'default';
}

/** A representative payload for the console's live preview AND per-template test
 *  sends, so what you preview is what the test delivers. `variant` selects the
 *  payment_action branch (sca vs declined). */
export function samplePayload(variant: string = 'default'): NotifyPayload {
  return {
    lot_name: 'Silk Wrap Dress',
    lot_number: '12',
    slug: 'silk-wrap-dress',
    amount_cents: 4800,
    shipping_cost: 600,
    bought_now: false,
    deadline: '2026-06-28T15:14:00.000Z',
    hours_left: 12,
    reason: variant === 'sca' ? 'requires_action' : 'failed',
  };
}
